import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { errorMessage, fieldErrors } from "../../shared/api/errors.ts";
import { refetchMessages } from "../../shared/messages/messages.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { BottomSheet, useCloseSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import styles from "../eva/RenameSheet.module.css";

/** Как у backend: `displayName` в UpdateMeRequest — max_length=128. */
const DISPLAY_NAME_MAX_LENGTH = 128;

type Errors = { displayName?: string; form?: string };

/**
 * «Изменить» (11.05): меняется только отображаемое имя — email, пароль и прочее из макета не выводятся.
 * Шторка-маршрут `/profile/edit` поверх профиля, каркас — как у переименования E-va.
 */
export function EditNameSheet() {
  const close = useCloseSheet("/profile");
  return (
    <BottomSheet open onClose={close} title="Изменить имя">
      <EditNameForm onDone={close} />
    </BottomSheet>
  );
}

function EditNameForm({ onDone }: { onDone: () => void }) {
  const { user, updateProfile } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(user?.displayName ?? "");
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useMutation({
    mutationFn: (displayName: string) => updateProfile({ displayName }),
    onSuccess: () => {
      // Имя автора приходит в карточках сообщений: ленты и открытые сообщения запрашиваются заново.
      void refetchMessages(queryClient);
      onDone();
    },
    onError: (error) => setErrors(errorsFrom(error)),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setErrors({ displayName: "Введите имя" });
      return;
    }
    mutation.mutate(trimmed);
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <Input
        label="Имя"
        name="displayName"
        autoComplete="name"
        maxLength={DISPLAY_NAME_MAX_LENGTH}
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          setErrors({});
        }}
        error={errors.displayName}
      />
      <div className={styles.actions}>
        <Button type="submit" loading={mutation.isPending}>
          Сохранить
        </Button>
        {errors.form && (
          <p className={styles.formError} role="alert">
            {errors.form}
          </p>
        )}
        <Button variant="secondary" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </form>
  );
}

function errorsFrom(error: Error): Errors {
  const byField = fieldErrors(error, ["displayName"] as const);
  return byField.displayName ? byField : { form: errorMessage(error) };
}
